import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { getOrders, updateOrderStatus, importMockOrders, createPickList, createBatchPickList, releaseOrderReservation, getOrderTimeline, bookOrderManualShipping, removeOrderManualShipping, generateInvoice, getInvoiceByOrder, invoicePdfUrl, lookupSerial } from '../lib/api';
import { Plus, Eye, Truck, ShoppingCart, CloudArrowDown, ListChecks, ArrowCounterClockwise, Stack, ClockCounterClockwise, Package, Barcode, Play, CheckCircle, ArrowRight, X, FileText } from '@phosphor-icons/react';
import HelpAnchor from '../components/HelpAnchor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ManualOrderDialog from '../components/ManualOrderDialog';
import FilterBar from '../components/FilterBar';
import BulkActionsBar from '../components/BulkActionsBar';
import useBulkSelection from '../lib/useBulkSelection';
import { matchesCreatedWindow, CREATED_WINDOW_OPTIONS } from '../lib/dateWindow';
import useUrlFilterState, { useUrlFilterClear } from '../lib/useUrlFilterState';

const statusColors = {
  pending: 'bg-amber-100 text-amber-800',
  picking: 'bg-blue-100 text-blue-800',
  packed: 'bg-green-100 text-green-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  on_hold: 'bg-red-100 text-red-800',
  cancelled: 'bg-muted text-slate-800',
  partial_backorder: 'bg-orange-100 text-orange-800',
  backordered: 'bg-rose-100 text-rose-800',
};

export default function Orders() {
  const { t } = useTranslation();
  const { isManager } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useUrlFilterState('status', '');
  // Iter 99 — local client-side filters to compose with the server-side status filter
  const [searchTerm, setSearchTerm] = useUrlFilterState('q', '');
  const [channelFilter, setChannelFilter] = useUrlFilterState('channel', '');
  const [dateWindow, setDateWindow] = useUrlFilterState('date', '');
  const clearUrlFilters = useUrlFilterClear(['q', 'status', 'channel', 'date']);
  const [showImport, setShowImport] = useState(false);
  const orderSel = useBulkSelection();
  const [timelineOrder, setTimelineOrder] = useState(null);
  const [shippingOrder, setShippingOrder] = useState(null); // Manual shipping target
  const [importChannel, setImportChannel] = useState('shopify');
  const [importCount, setImportCount] = useState(5);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('stockaris_orders_view') || 'list'; } catch { return 'list'; }
  });

  const switchView = (m) => {
    setViewMode(m);
    try { localStorage.setItem('stockaris_orders_view', m); } catch { /* ignore */ }
  };

  const fetchData = async () => {
    try {
      const res = await getOrders({ status: statusFilter || undefined });
      setOrders(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  // Compose: server-filtered orders narrowed further client-side by search + channel
  const filteredOrders = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return orders.filter((o) => {
      if (term) {
        const hay = `${o.order_number || ''} ${o.customer_name || ''} ${o.customer_email || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (channelFilter && (o.channel || 'manual') !== channelFilter) return false;
      if (!matchesCreatedWindow(o.created_at, dateWindow)) return false;
      return true;
    });
  }, [orders, searchTerm, channelFilter, dateWindow]);

  const uniqueChannels = React.useMemo(() => {
    const set = new Set(orders.map(o => o.channel || 'manual'));
    return Array.from(set).sort();
  }, [orders]);

  const handleImport = async () => {
    try {
      await importMockOrders(importChannel, importCount);
      setShowImport(false);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreatePickList = async () => {
    if (orderSel.count === 0) return;
    try {
      await createPickList({ order_ids: orderSel.ids });
      orderSel.clear();
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateBatchPickList = async () => {
    if (orderSel.count < 2) return;
    try {
      await createBatchPickList({ order_ids: orderSel.ids });
      orderSel.clear();
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleBulkCancel = async () => {
    if (orderSel.count === 0) return;
    if (!window.confirm(`Cancel ${orderSel.count} order${orderSel.count === 1 ? '' : 's'}? Inventory reservations will be released.`)) return;
    let ok = 0; let failed = 0;
    for (const id of orderSel.ids) {
      try {
        await updateOrderStatus(id, 'cancelled');
        ok++;
      } catch (e) {
        console.error(e);
        failed++;
      }
    }
    orderSel.clear();
    if (failed > 0) toast.error(`Cancelled ${ok}, ${failed} failed`);
    else toast.success(`${ok} order${ok === 1 ? '' : 's'} cancelled`);
    fetchData();
  };

  const openTimeline = async (orderId) => {
    setTimelineOrder({ loading: true, id: orderId });
    try {
      const res = await getOrderTimeline(orderId);
      setTimelineOrder(res.data);
    } catch (e) {
      console.error(e);
      setTimelineOrder(null);
    }
  };

  const handleReleaseReservation = async (orderId) => {
    if (!window.confirm('Release inventory reservation for this order?')) return;
    try {
      await releaseOrderReservation(orderId);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Open the order's tax invoice in a new tab.
   *
   * Idempotent — if a draft already exists from pack-complete (or an issued
   * one from ship-now), we just open its PDF. Otherwise we generate the
   * draft on the fly and open that. Most operators will never need to
   * click this on a 'shipped' order — the auto-finalise on ship covers it.
   */
  const handleOpenInvoice = async (order) => {
    try {
      const r = await getInvoiceByOrder(order.id);
      let inv = r.data?.invoice;
      if (!inv) {
        const g = await generateInvoice(order.id);
        inv = g.data;
      }
      if (!inv?.id) {
        toast.error('Invoice not available');
        return;
      }
      window.open(invoicePdfUrl(inv.id), '_blank', 'noopener');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((x) => x?.msg || JSON.stringify(x)).join('; ')
          : detail?.msg || e?.message || 'Could not open invoice';
      toast.error(msg);
    }
  };

  const toggleOrderSelect = (orderId) => orderSel.toggle(orderId);

  const pendingOrders = orders.filter(o => o.status === 'pending');

  return (
    <Layout>
      <div data-testid="orders-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('orders.page.title')}</h1>
            <p className="text-muted-foreground">{t('orders.page.subtitle')}</p>
          </div>
          {isManager && (
            <div className="flex gap-2 flex-wrap">
              <Dialog open={showImport} onOpenChange={setShowImport}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-orders-btn">
                    <CloudArrowDown size={18} className="mr-2" /> {t('orders.actions.import_mock')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="border border-border rounded-none">
                  <DialogHeader>
                    <DialogTitle>{t('orders.import_dialog.title')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="overline">{t('orders.import_dialog.channel_label')}</Label>
                      <Select value={importChannel} onValueChange={setImportChannel}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shopify">Shopify</SelectItem>
                          <SelectItem value="etsy">Etsy</SelectItem>
                          <SelectItem value="amazon">Amazon</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="overline">Number of Orders</Label>
                      <Input type="number" value={importCount} onChange={(e) => setImportCount(parseInt(e.target.value))} min={1} max={20} className="mt-1" />
                    </div>
                    <Button onClick={handleImport} className="w-full bg-brand hover:bg-brand/90">Import {importCount} Orders</Button>
                  </div>
                </DialogContent>
              </Dialog>
              <ManualOrderDialog onCreated={fetchData} />
            </div>
          )}
        </div>

        {/* Sticky bulk-actions bar — appears when 1+ orders are selected. Pick list,
            optional Batch pick (≥2 selected), and Cancel. */}
        <BulkActionsBar
          count={orderSel.count}
          onClear={orderSel.clear}
          actions={[
            {
              key: 'picklist',
              label: 'Create pick list',
              icon: <ListChecks size={14} weight="fill" />,
              onClick: handleCreatePickList,
              testId: 'create-picklist-btn',
              title: `Create a pick list for ${orderSel.count} order${orderSel.count === 1 ? '' : 's'}`,
            },
            ...(orderSel.count >= 2 ? [{
              key: 'batch',
              label: `Batch pick (${orderSel.count})`,
              icon: <Stack size={14} weight="fill" />,
              onClick: handleCreateBatchPickList,
              testId: 'create-batch-picklist-btn',
              title: 'Group items across orders into one pick walk',
            }] : []),
            {
              key: 'cancel',
              label: 'Cancel',
              icon: <X size={14} weight="bold" />,
              onClick: handleBulkCancel,
              testId: 'bulk-cancel-orders-btn',
              title: 'Cancel selected orders & release reservations',
            },
          ]}
        />

        {/* View toggle: List ↔ Board */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex bg-muted rounded-md p-0.5 border border-border" data-testid="orders-view-toggle">
            <button
              type="button"
              onClick={() => switchView('list')}
              data-testid="orders-view-list"
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => switchView('board')}
              data-testid="orders-view-board"
              className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                viewMode === 'board'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Board
            </button>
          </div>
          <span className="text-xs text-muted-foreground">
            {viewMode === 'list' ? 'Tabular view — best for selecting and acting in bulk.' : 'Kanban board — best for spotting bottlenecks across the fulfillment pipeline.'}
          </span>
        </div>

        {viewMode === 'board' ? (
          <OrdersKanban
            orders={orders}
            loading={loading}
            statusColors={statusColors}
            onOpenTimeline={async (o) => {
              try {
                const res = await getOrderTimeline(o.id);
                setTimelineOrder({ ...o, ...res.data });
              } catch (e) { console.error(e); }
            }}
            onAdvanceStatus={async (orderId, newStatus) => {
              // Optimistic update: bump UI first, rollback on API failure.
              const prev = orders;
              setOrders((curr) => curr.map((x) => x.id === orderId ? { ...x, status: newStatus } : x));
              try {
                await updateOrderStatus(orderId, newStatus);
                toast.success(`Order moved to ${newStatus.toUpperCase()}`);
              } catch (err) {
                console.error(err);
                setOrders(prev);
                toast.error(err?.response?.data?.detail || 'Status update failed — rolled back');
              }
            }}
          />
        ) : (
        <>
        <FilterBar
          testId="orders-filter"
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: t('orders.filters.search_placeholder') }}
          filters={[
            {
              key: 'channel', value: channelFilter, onChange: setChannelFilter, label: t('orders.cols.channel'),
              options: [
                { value: '', label: t('orders.filters.channel_all') },
                ...uniqueChannels.map(c => ({ value: c, label: c })),
              ],
            },
            {
              key: 'date', value: dateWindow, onChange: setDateWindow, label: t('analytics.filters.window'),
              options: CREATED_WINDOW_OPTIONS,
            },
          ]}
          onClear={clearUrlFilters}
        />
        <SerialLookupBar />
        <Tabs defaultValue="all" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none flex-wrap">
            <TabsTrigger value="all" onClick={() => setStatusFilter('')} className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">All ({orders.length})</TabsTrigger>
            <TabsTrigger value="pending" onClick={() => setStatusFilter('pending')} className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">{t('orders.status.pending')}</TabsTrigger>
            <TabsTrigger value="picking" onClick={() => setStatusFilter('picking')} className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">{t('orders.status.picking')}</TabsTrigger>
            <TabsTrigger value="packed" onClick={() => setStatusFilter('packed')} className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">{t('orders.status.packed')}</TabsTrigger>
            <TabsTrigger value="shipped" onClick={() => setStatusFilter('shipped')} className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">{t('orders.status.shipped')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="p-0 m-0">
            {/* Desktop table (hidden on phone) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th className="w-10"></th>
                    <th>{t('orders.cols.order_number')}</th>
                    <th>{t('orders.cols.channel')}</th>
                    <th>{t('orders.cols.customer')}</th>
                    <th>Items</th>
                    <th>{t('orders.cols.status')}</th>
                    <th>{t('orders.cols.created_at')}</th>
                    <th>{t('orders.cols.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">
                      {orders.length === 0 ? 'No orders found' : 'No orders match current filters'}
                    </td></tr>
                  ) : filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td>
                        {order.status === 'pending' && (
                          <Checkbox
                            data-testid={`order-checkbox-${order.order_number}`}
                            checked={orderSel.isSelected(order.id)}
                            onCheckedChange={() => toggleOrderSelect(order.id)}
                          />
                        )}
                      </td>
                      <td className="font-mono font-semibold">{order.order_number}</td>
                      <td><span className="text-xs uppercase font-bold text-muted-foreground">{order.channel}</span></td>
                      <td>{order.customer_name}</td>
                      <td className="font-mono">{order.items?.length || 0}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${statusColors[order.status] || 'bg-muted'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openTimeline(order.id)}
                            className="p-1.5 hover:bg-muted"
                            title="View Timeline"
                            data-testid={`view-timeline-${order.order_number}`}
                          >
                            <Eye size={16} />
                          </button>
                          {order.status !== 'cancelled' && (
                            <button
                              onClick={() => setShippingOrder(order)}
                              data-testid={`manual-ship-${order.order_number}`}
                              className={`p-1.5 hover:bg-blue-50 ${order.manual_shipping ? 'text-brand' : 'text-muted-foreground'}`}
                              title={order.manual_shipping
                                ? `Shipped via ${order.manual_shipping.carrier_name}${order.manual_shipping.tracking_number ? ' · ' + order.manual_shipping.tracking_number : ''}`
                                : 'Book manual shipping'}
                            >
                              <Truck size={16} weight={order.manual_shipping ? 'fill' : 'regular'} />
                            </button>
                          )}
                          {['on_hold', 'cancelled', 'pending'].includes(order.status) && !order.reservation_released && (
                            <button
                              onClick={() => handleReleaseReservation(order.id)}
                              className="p-1.5 hover:bg-amber-50 text-amber-600"
                              title="Release Reservation"
                              data-testid={`release-reservation-${order.id}`}
                            >
                              <ArrowCounterClockwise size={16} />
                            </button>
                          )}
                          {['packed', 'shipped'].includes(order.status) && (
                            <button
                              onClick={() => handleOpenInvoice(order)}
                              data-testid={`order-invoice-${order.order_number}`}
                              title={order.status === 'shipped' ? 'View / print tax invoice' : 'View / print draft tax invoice'}
                              className={`p-1.5 hover:bg-violet-50 ${order.status === 'shipped' ? 'text-violet-700' : 'text-violet-400'}`}
                            >
                              <FileText size={16} weight={order.status === 'shipped' ? 'fill' : 'regular'} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile card view (<md) */}
            <div className="md:hidden divide-y divide-border" data-testid="orders-mobile-list">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  {orders.length === 0 ? 'No orders found' : 'No orders match current filters'}
                </div>
              ) : filteredOrders.map(order => (
                <div
                  key={order.id}
                  className="p-4 flex items-start gap-3"
                  data-testid={`order-card-${order.order_number}`}
                  onClick={() => openTimeline(order.id)}
                >
                  {order.status === 'pending' && (
                    <div onClick={(e) => e.stopPropagation()} className="pt-1">
                      <Checkbox
                        data-testid={`order-checkbox-m-${order.order_number}`}
                        checked={orderSel.isSelected(order.id)}
                        onCheckedChange={() => toggleOrderSelect(order.id)}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-mono font-bold text-sm truncate">{order.order_number}</span>
                      <span className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase ${statusColors[order.status] || 'bg-muted'}`}>
                        {order.status}
                      </span>
                    </div>
                    <p className="text-sm truncate">{order.customer_name || '—'}</p>
                    <div className="flex items-center justify-between mt-1 text-[11px] font-mono text-muted-foreground">
                      <span>{(order.channel || 'manual').toUpperCase()} · {order.items?.length || 0} items</span>
                      <span>{new Date(order.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
        </>
        )}

        {/* Timeline Dialog */}
        <Dialog open={!!timelineOrder} onOpenChange={(o) => !o && setTimelineOrder(null)}>
          <DialogContent className="border border-border rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-timeline-dialog">
            <DialogHeader>
              <DialogTitle>
                Order Timeline {timelineOrder?.order?.order_number && `· ${timelineOrder.order.order_number}`}
              </DialogTitle>
            </DialogHeader>
            {timelineOrder?.loading ? (
              <div className="py-12 text-center text-muted-foreground">Loading timeline...</div>
            ) : timelineOrder?.order ? (
              <div>
                {/* Order summary */}
                <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-muted border border-border">
                  <div>
                    <p className="overline">Customer</p>
                    <p className="font-bold">{timelineOrder.order.customer_name || '-'}</p>
                  </div>
                  <div>
                    <p className="overline">Status</p>
                    <p className="font-bold">
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${statusColors[timelineOrder.order.status] || 'bg-muted'}`}>
                        {timelineOrder.order.status}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="overline">Channel</p>
                    <p className="font-mono uppercase">{timelineOrder.order.channel}</p>
                  </div>
                  <div>
                    <p className="overline">Items</p>
                    <p className="font-mono font-bold">{timelineOrder.order.items?.length || 0}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="overline">Shipping Address</p>
                    <p className="text-sm">{timelineOrder.order.shipping_address || '-'}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="relative pl-6 border-l-2 border-border">
                  {(timelineOrder.events || []).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No events yet</p>
                  ) : (timelineOrder.events || []).map((ev, idx) => {
                    const iconMap = {
                      plus: Plus, hand: ListChecks, play: Play, check: CheckCircle,
                      package: Package, barcode: Barcode, truck: Truck, arrow: ArrowRight,
                    };
                    const Icon = iconMap[ev.icon] || Eye;
                    const colors = {
                      created: 'bg-brand',
                      pick_created: 'bg-amber-600',
                      pick_started: 'bg-blue-500',
                      pick_completed: 'bg-green-600',
                      pack_created: 'bg-amber-500',
                      pack_completed: 'bg-green-600',
                      label_generated: 'bg-brand',
                      shipped: 'bg-indigo-600',
                    };
                    const dotColor = colors[ev.type] || 'bg-muted0';
                    return (
                      <div key={idx} className="relative mb-6" data-testid={`timeline-event-${idx}`}>
                        <div className={`absolute -left-[34px] top-0 w-6 h-6 ${dotColor} flex items-center justify-center`}>
                          <Icon size={14} weight="bold" color="white" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">{ev.title}</p>
                          <p className="text-sm text-muted-foreground">{ev.description}</p>
                          <p className="text-xs text-muted-foreground font-mono mt-0.5">
                            {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '-'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Manual shipping dialog */}
        <OrderManualShippingDialog
          order={shippingOrder}
          onClose={() => setShippingOrder(null)}
          onSaved={async () => { setShippingOrder(null); await fetchData(); }}
          isManager={isManager}
        />
      </div>
    </Layout>
  );
}

const OrderManualShippingDialog = ({ order, onClose, onSaved, isManager }) => {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(order?.manual_shipping || {}); }, [order]);
  if (!order) return null;
  const editable = isManager || order.status !== 'cancelled';
  const submit = async () => {
    if (!form.carrier_name?.trim()) return toast.error('Carrier name required');
    try {
      await bookOrderManualShipping(order.id, {
        carrier_name: form.carrier_name.trim(),
        tracking_number: form.tracking_number || null,
        service_level: form.service_level || null,
        shipping_cost: form.shipping_cost !== '' && form.shipping_cost != null ? Number(form.shipping_cost) : null,
        shipping_currency: form.shipping_currency || 'USD',
        ship_date: form.ship_date ? new Date(form.ship_date).toISOString() : null,
        estimated_delivery_at: form.estimated_delivery_at ? new Date(form.estimated_delivery_at).toISOString() : null,
        notes: form.notes || null,
        contact_name: form.contact_name || null,
        contact_phone: form.contact_phone || null,
        cartons: form.cartons !== '' && form.cartons != null ? Number(form.cartons) : null,
        weight_kg: form.weight_kg !== '' && form.weight_kg != null ? Number(form.weight_kg) : null,
      });
      toast.success('Manual shipping saved');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async () => {
    if (!window.confirm('Remove manual shipping from this order?')) return;
    try { await removeOrderManualShipping(order.id); toast.success('Removed'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border border-border rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="order-shipping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={18} weight="fill" className="text-brand" /> Manual shipping · {order.order_number}
          </DialogTitle>
          <DialogDescription className="text-xs">Per-order carrier details for carton shipments not going via a container dispatch.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Carrier *</Label><Input value={form.carrier_name || ''} onChange={(e) => setForm({ ...form, carrier_name: e.target.value })} disabled={!editable} className="rounded-none" data-testid="ord-ship-carrier" /></div>
          <div><Label>Tracking #</Label><Input value={form.tracking_number || ''} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} disabled={!editable} className="rounded-none font-mono" data-testid="ord-ship-tracking" /></div>
          <div><Label>Service level</Label><Input value={form.service_level || ''} onChange={(e) => setForm({ ...form, service_level: e.target.value })} disabled={!editable} className="rounded-none" placeholder="Express / Standard" /></div>
          <div><Label>Cost</Label>
            <div className="flex gap-1">
              <Input type="number" step="0.01" value={form.shipping_cost ?? ''} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} disabled={!editable} className="rounded-none flex-1" />
              <Input value={form.shipping_currency || 'USD'} onChange={(e) => setForm({ ...form, shipping_currency: e.target.value.toUpperCase().slice(0, 4) })} disabled={!editable} className="rounded-none w-16 font-mono" />
            </div>
          </div>
          <div><Label>Cartons</Label><Input type="number" value={form.cartons ?? ''} onChange={(e) => setForm({ ...form, cartons: e.target.value })} disabled={!editable} className="rounded-none" /></div>
          <div><Label>Total weight (kg)</Label><Input type="number" step="0.01" value={form.weight_kg ?? ''} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} disabled={!editable} className="rounded-none" /></div>
          <div><Label>Ship date</Label><Input type="datetime-local" value={form.ship_date ? new Date(form.ship_date).toISOString().slice(0, 16) : ''} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} disabled={!editable} className="rounded-none" /></div>
          <div><Label>ETA</Label><Input type="datetime-local" value={form.estimated_delivery_at ? new Date(form.estimated_delivery_at).toISOString().slice(0, 16) : ''} onChange={(e) => setForm({ ...form, estimated_delivery_at: e.target.value })} disabled={!editable} className="rounded-none" /></div>
          <div><Label>Contact name</Label><Input value={form.contact_name || ''} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} disabled={!editable} className="rounded-none" /></div>
          <div><Label>Contact phone</Label><Input value={form.contact_phone || ''} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} disabled={!editable} className="rounded-none font-mono" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!editable} className="rounded-none" /></div>
        </div>
        {editable && (
          <div className="flex gap-2 mt-3">
            <Button onClick={submit} data-testid="ord-submit-shipping" className="bg-brand text-white rounded-none flex-1">
              {order.manual_shipping ? 'Update' : 'Book shipping'}
            </Button>
            {order.manual_shipping && (
              <Button onClick={remove} variant="outline" className="rounded-none border-rose-300 text-rose-700">Remove</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================================
   Orders Kanban — 5-column status board (Pending · Picking · Packed · Shipped · On Hold)
============================================================================ */
function OrdersKanban({ orders, loading, statusColors, onOpenTimeline, onAdvanceStatus }) {
  const [dragOver, setDragOver] = React.useState(null);

  const columns = [
    { key: 'pending', label: 'Pending', tint: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900' },
    { key: 'picking', label: 'Picking', tint: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900' },
    { key: 'packed', label: 'Packed', tint: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900' },
    { key: 'shipped', label: 'Shipped', tint: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-900' },
    { key: 'on_hold', label: 'On Hold', tint: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900' },
  ];

  const grouped = columns.reduce((acc, c) => { acc[c.key] = []; return acc; }, {});
  for (const o of orders) {
    if (grouped[o.status]) grouped[o.status].push(o);
  }

  const onDragStart = (e, orderId, fromStatus) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ orderId, fromStatus }));
  };

  const onDrop = (e, toStatus) => {
    e.preventDefault();
    setDragOver(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    if (!payload?.orderId || payload.fromStatus === toStatus) return;
    onAdvanceStatus?.(payload.orderId, toStatus);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center text-muted-foreground" data-testid="orders-kanban-loading">
        Loading orders…
      </div>
    );
  }

  return (
    <div
      data-testid="orders-kanban"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3"
    >
      {columns.map((col) => {
        const items = grouped[col.key];
        const isOver = dragOver === col.key;
        return (
          <div
            key={col.key}
            data-testid={`kanban-col-${col.key}`}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(col.key); }}
            onDragLeave={() => setDragOver((prev) => (prev === col.key ? null : prev))}
            onDrop={(e) => onDrop(e, col.key)}
            className={`rounded-lg border ${col.tint} flex flex-col transition-all duration-150 ${
              isOver ? 'ring-2 ring-brand ring-offset-2 ring-offset-background scale-[1.01]' : ''
            }`}
            style={{ minHeight: 360 }}
          >
            <div className="px-3 py-2 border-b border-border/50 flex items-center justify-between rounded-t-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground">{col.label}</span>
                <span data-testid={`kanban-count-${col.key}`} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-card border border-border text-muted-foreground">
                  {items.length}
                </span>
              </div>
            </div>
            <div className="p-2 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 360px)' }}>
              {items.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic px-2 py-3 text-center">
                  {isOver ? 'Drop here to move →' : 'No orders in this stage.'}
                </div>
              ) : items.slice(0, 50).map((o) => (
                <div
                  key={o.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, o.id, col.key)}
                  data-testid={`kanban-card-${o.order_number}`}
                  data-draggable="true"
                  onClick={() => onOpenTimeline(o)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenTimeline(o); } }}
                  role="button"
                  tabIndex={0}
                  className="w-full text-left bg-card border border-border rounded-md p-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-grab active:cursor-grabbing"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-semibold text-[12px] text-foreground truncate">{o.order_number}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${statusColors[o.status] || ''}`}>
                      {o.status}
                    </span>
                  </div>
                  <div className="text-[12px] text-foreground truncate">{o.customer_name || '—'}</div>
                  <div className="flex items-center justify-between mt-2 text-[10px] font-mono text-muted-foreground">
                    <span>{o.channel?.toUpperCase() || 'MANUAL'}</span>
                    <span>{(o.items?.length || 0)} items</span>
                  </div>
                </div>
              ))}
              {items.length > 50 && (
                <div className="text-[10px] text-muted-foreground italic text-center pt-1">
                  +{items.length - 50} more — use List view to act on the full set.
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}



/**
 * SerialLookupBar — quick "trace a serial back to its order" widget.
 *
 * Support flow: customer has a broken unit + a serial sticker. Paste the
 * serial here, get the order + dispatch + current status. Clicking through
 * navigates the page to filter on that order number.
 */
function SerialLookupBar() {
  const [serial, setSerial] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const doLookup = async () => {
    const q = serial.trim();
    if (!q) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await lookupSerial(q);
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || `Serial '${q}' not found`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-3 p-3 border border-dashed border-brand/40 bg-brand/5 flex flex-col sm:flex-row gap-2 items-start sm:items-center" data-testid="orders-serial-lookup">
      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-brand">
        <Barcode size={14} weight="fill" />
        Look up by serial
        <HelpAnchor articleId="serials" label="How serial lookup works" className="ml-1" />
      </div>
      <Input
        placeholder="Paste or scan a serial (e.g. IMEI-123456789012345)"
        value={serial}
        onChange={(e) => setSerial(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && doLookup()}
        className="max-w-sm font-mono text-xs"
        data-testid="orders-serial-lookup-input"
      />
      <Button size="sm" onClick={doLookup} disabled={busy || !serial.trim()} data-testid="orders-serial-lookup-btn">
        {busy ? 'Searching…' : 'Trace'}
      </Button>
      {error && (
        <span className="text-xs text-rose-700 font-mono" data-testid="orders-serial-lookup-error">
          {error}
        </span>
      )}
      {result && (
        <div className="text-xs flex flex-wrap items-center gap-3" data-testid="orders-serial-lookup-result">
          <span className="font-mono font-bold">{result.serial?.serial_no}</span>
          <span className="px-2 py-0.5 border font-black uppercase tracking-widest text-[10px] bg-white">
            {result.serial?.status || 'unknown'}
          </span>
          {result.serial?.sku_code && (
            <span className="font-mono text-muted-foreground">SKU {result.serial.sku_code}</span>
          )}
          {result.order ? (
            <>
              <span>→</span>
              <span className="font-bold">Order {result.order.order_number}</span>
              <span className="text-muted-foreground">({result.order.status})</span>
              {result.order.customer_name && (
                <span className="text-muted-foreground">· {result.order.customer_name}</span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">— never assigned to an order</span>
          )}
          {result.dispatch && (
            <span className="text-muted-foreground">
              · shipped on {result.dispatch.dispatch_number}
            </span>
          )}
          {result.serial?.rma_note && (
            <span className="px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-800 text-[10px] font-black uppercase tracking-widest">
              RMA: {result.serial.rma_note.slice(0, 40)}
            </span>
          )}
          <button
            type="button"
            onClick={() => { setSerial(''); setResult(null); setError(''); }}
            className="text-muted-foreground hover:text-foreground"
            data-testid="orders-serial-lookup-clear"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

